import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router';
import {Alert, Box, Button, CircularProgress, IconButton, Input, LinearProgress, Sheet, Skeleton, Typography} from '@mui/joy';
import FolderRounded from '@mui/icons-material/FolderRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import GridViewRounded from '@mui/icons-material/GridViewRounded';
import ViewListRounded from '@mui/icons-material/ViewListRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import './clientMaterials.css';
import {API_URL} from '../api';
import {requestJSON, checkSignal} from './direct';
import {clientSections, clientFileURL, folderPrefix, guestFolderPrefix, sharedFolderPrefix, previewable} from './clientFolderApi';
import ImageViewer from './ImageViewer';
import {runArchive, archiveStatus} from './archive';
import StraightenRounded from '@mui/icons-material/StraightenRounded';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import FolderCopyOutlined from '@mui/icons-material/FolderCopyOutlined';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import {canDownloadMaterial, materialSize, materialSummary, materialType, planPreview, selectionState, visibleMaterials} from './folderPresentation';

const labels = {materials: 'Все материалы', input: 'Вводные данные', measurement: 'Обмер', result: 'Чертежи'};
const sectionIcons = {materials: <FolderOutlined/>, input: <FolderCopyOutlined/>, measurement: <StraightenRounded/>, result: <InsertDriveFileRounded/>};
const viewKey = 'planium-client-media-view';
function storedView() {try {return localStorage.getItem(viewKey) === 'list' ? 'list' : 'grid';} catch {return 'grid';}}

function PhotoThumbnail({node, resolveSource}) {
  const element = useRef(null), [url, setURL] = useState(''), [visible, setVisible] = useState(false), [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!window.IntersectionObserver) {setVisible(true); return undefined;}
    const observer = new window.IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {setVisible(true); observer.disconnect();}
    }, {rootMargin: '180px'});
    if (element.current) observer.observe(element.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return undefined;
    const controller = new AbortController();
    setURL(''); setFailed(false);
    resolveSource(node, controller.signal).then(value => {if (!controller.signal.aborted) setURL(value);})
      .catch(() => {if (!controller.signal.aborted) setFailed(true);});
    return () => controller.abort();
  }, [visible, node.id, node.version, resolveSource]);
  return <span ref={element} className={`cm-photo${planPreview(node) ? ' is-plan' : ''}`} aria-hidden="true">{url && !failed
    ? <img src={url} alt="" crossOrigin="anonymous" referrerPolicy="no-referrer" loading="lazy" decoding="async" onError={() => setFailed(true)}/>
    : <><ImageRounded/>{failed && <span className="cm-preview-failed">Превью недоступно</span>}</>}</span>;
}

export default function HomeMediaFolder({getToken, backPath, audience = 'client', signedIn = false, onSessionExpired}) {
  const {objectId, section: routeSection, shareToken: materialsToken, folderShareToken} = useParams();
  const guest = audience === 'guest' || audience === 'share';
  const shareToken = audience === 'share' ? folderShareToken : materialsToken;
  const location = useLocation();
  const initialFolder = guest ? new URLSearchParams(location.search).get('folder') || '' : '';
  const validInitialFolder = !initialFolder || /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(initialFolder);
  const navigate = useNavigate();
  const section = guest ? 'materials' : routeSection || 'materials';
  const token = getToken ? getToken() : localStorage.getItem('client_token');
  const scope = guest ? `customer:${audience}:${shareToken}:${initialFolder}:${token}:${signedIn}` : `${objectId}:${section}:${token}`;
  const validRoute = guest ? /^[A-Za-z0-9_-]{32,128}$/.test(String(shareToken)) && validInitialFolder
    : /^[1-9]\d*$/.test(String(objectId)) && clientSections.includes(section);
  const prefix = validRoute ? (audience === 'share' ? sharedFolderPrefix(shareToken) : guest ? guestFolderPrefix(shareToken) : folderPrefix(objectId, section)) : '';
  const [view, setView] = useState(storedView);
  const [query, setQuery] = useState(''), [ordering, setOrdering] = useState('name');
  const [searchExpanded, setSearchExpanded] = useState(false);
  useEffect(() => {try {localStorage.setItem(viewKey, view);} catch { /* Private browser mode. */ }}, [view]);
  const [selection, setSelection] = useState({scope, id: initialFolder});
  const folder = selection.scope === scope ? selection.id : initialFolder;
  const [result, setResult] = useState(null), [error, setError] = useState('');
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true), [activeFile, setActiveFile] = useState('');
  const [refresh, setRefresh] = useState(0), [viewer, setViewer] = useState(null);
  const downloadController = useRef(null), archiveController = useRef(null);
  const [selected, setSelected] = useState([]), [zipBusy, setZipBusy] = useState(false), [zipHint, setZipHint] = useState('');
  const selectAllInput = useRef(null), initialLanding = useRef(new Set());
  const data = result?.scope === scope && result?.folder === folder ? result.value : null;
  const hasPending = Boolean(data?.items.some(node => node.transfer_state === 'pending'));
  const viewerScope = `${scope}:${folder}`;
  const clearRestrictedData = useCallback(() => {
    setResult(null); setCategories(null); setViewer(null); setSelected([]); setZipBusy(false); setZipHint(''); setActiveFile('');
    downloadController.current?.abort(); archiveController.current?.abort(); archiveController.current = null;
  }, []);

  useEffect(() => {
    setViewer(null); setActiveFile(''); setSelected([]); setQuery(''); setSearchExpanded(false); setZipBusy(false); setZipHint('');
    downloadController.current?.abort(); archiveController.current?.abort();
    return () => {downloadController.current?.abort(); archiveController.current?.abort();};
  }, [scope, folder]);

  useEffect(() => {
    const controller = new AbortController();
    setError(''); setLoading(true);
    if (!validRoute || !token || (guest && !signedIn)) {
      clearRestrictedData();
      setError(!validRoute ? 'Некорректная ссылка на папку.' : 'Войдите в свой кабинет, чтобы открыть материалы.');
      setLoading(false);
      return () => controller.abort();
    }
    requestJSON(API_URL + prefix + (folder ? `?folder=${encodeURIComponent(folder)}` : ''), {
      headers: {Authorization: `ClientToken ${token}`}, cache: 'no-store',
    }, controller.signal)
      .then(value => {
        if (!Array.isArray(value.items) || !Array.isArray(value.rights) || !Array.isArray(value.breadcrumbs)) {
          throw new Error('Сервер вернул некорректный список файлов.');
        }
        if (!controller.signal.aborted) setResult({scope, folder, value});
      })
      .catch(failure => {
        if (controller.signal.aborted) return;
        if ([401, 403, 404].includes(failure.status)) clearRestrictedData();
        if (failure.status === 401) onSessionExpired?.();
        setError(failure.message || 'Не удалось открыть папку. Повторите позже.');
      })
      .finally(() => {if (!controller.signal.aborted) setLoading(false);});
    return () => controller.abort();
  }, [scope, prefix, folder, refresh, token, validRoute, guest, signedIn, onSessionExpired, clearRestrictedData]);

  useEffect(() => {
    if (!hasPending) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden') setRefresh(value => value + 1);
    }, 20000);
    return () => clearInterval(timer);
  }, [hasPending]);

  const resolveSource = useCallback((node, signal) => clientFileURL(API_URL, prefix, node, token, true, signal), [prefix, token]);
  const downloadFile = useCallback(async (node, signal) => {
    const url = await clientFileURL(API_URL, prefix, node, token, false, signal);
    checkSignal(signal);
    const anchor = document.createElement('a');
    anchor.href = url; anchor.rel = 'noreferrer noopener'; anchor.referrerPolicy = 'no-referrer';
    document.body.appendChild(anchor); anchor.click(); anchor.remove();
  }, [prefix, token]);
  async function save(node) {
    if (activeFile) return;
    const controller = new AbortController(); downloadController.current = controller;
    setError(''); setActiveFile(node.id);
    try {await downloadFile(node, controller.signal);}
    catch (failure) {if (!controller.signal.aborted) {
      if ([401, 403, 404].includes(failure.status)) clearRestrictedData();
      if (failure.status === 401) onSessionExpired?.();
      setError(failure.message || 'Не удалось получить файл.');
    }}
    finally {if (!controller.signal.aborted) setActiveFile('');}
  }
  const canDownload = node => canDownloadMaterial(node, data?.rights);
  const archiveAllowed = !guest && data?.can_archive !== false;
  const visible = visibleMaterials(data?.items, query, ordering);
  const selectable = archiveAllowed ? visible.filter(canDownload) : [];
  const selectionView = selectionState(selectable, selected);
  useEffect(() => {
    if (selectAllInput.current) selectAllInput.current.indeterminate = selectionView.partial;
  }, [selectionView.partial]);
  useEffect(() => {
    if (!data) return;
    const allowed = new Set(data.items.filter(node => canDownloadMaterial(node, data.rights)).map(node => node.id));
    setSelected(previous => previous.filter(id => allowed.has(id)));
  }, [data]);
  useEffect(() => {
    if (!data || section === 'materials') return;
    const children = data.items.filter(node => node.kind === 'folder');
    if (children.length) setCategories({scope, parent: data.folder.id, items: children});
    else setCategories(previous => previous?.scope !== scope || previous.parent === data.folder.id || !data.breadcrumbs.some(crumb => crumb.id === previous.parent) ? null : previous);
  }, [data, section, scope]);
  useEffect(() => {
    if (!data || guest || folder) return;
    const landingKey = `${objectId}:${token}`;
    // Direct links and explicit tabs must not later be mistaken for an initial overview visit.
    if (section !== 'materials') initialLanding.current.add(landingKey);
    if (section === 'materials' && !initialLanding.current.has(landingKey)) {
      initialLanding.current.add(landingKey);
      // Only a server-published measurement may become the initial view.
      if (data.measurement_ready && data.available_sections?.includes('measurement')) {
        navigate(`/lk/storage/${objectId}/measurement`, {replace: true, state: {materialsLanding: true}});
      }
    } else if (section === 'measurement' && location.state?.materialsLanding) {
      const folders = data.items.filter(node => node.kind === 'folder');
      const first = folders.find(node => /(?:обмер|лист|sheet)/i.test(node.name))
        || folders.find(node => /(?:фото|photo)/i.test(node.name));
      if (first) setSelection({scope, id: first.id});
      navigate(location.pathname, {replace: true, state: null});
    }
  }, [data, guest, folder, objectId, token, section, navigate, location.pathname, location.state, scope]);
  const toggle = id => setSelected(previous => previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id]);
  async function zip(ids) {
    if (!archiveAllowed || archiveController.current || !ids.length) return;
    const controller = new AbortController(); archiveController.current = controller;
    setZipBusy(true); setZipHint('Подготавливаем ZIP…'); setError('');
    try {
      await runArchive(API_URL, token, {scope: 'client', object_id: Number(objectId), section, ids}, {
        audience: 'client', signal: controller.signal, onUpdate: job => {if (!controller.signal.aborted) setZipHint(archiveStatus(job));}});
    } catch (failure) {if (!controller.signal.aborted) {
      setError(failure.message || 'Не удалось подготовить ZIP.');
      if ([401, 403, 404].includes(failure.status)) clearRestrictedData();
    }}
    finally {if (archiveController.current === controller) archiveController.current = null; if (!controller.signal.aborted) setZipBusy(false);}
  }
  const photos = visible.filter(node => previewable(node) && canDownload(node));
  const open = node => {
    if (node.kind === 'folder') {setSelection({scope, id: node.id}); return;}
    if (!canDownload(node)) return;
    if (previewable(node)) setViewer({scope: viewerScope, images: photos, startId: node.id});
    else save(node);
  };
  const changeQuery = value => {setQuery(value); setSelected([]);};
  const pendingCount = data?.items.filter(node => node.transfer_state === 'pending').length || 0;
  const downloadable = Boolean(data?.rights.includes('download'));
  const groupedGrid = view === 'grid' && visible.some(node => node.kind === 'folder') && visible.some(node => node.kind === 'file');
  const summary = materialSummary(data?.items);
  const selectedNodes = (data?.items || []).filter(node => selected.includes(node.id));
  const selectedSummary = materialSummary(selectedNodes);
  const categoryNodes = categories?.scope === scope ? categories.items : [];
  const applicationId = data?.application?.id || objectId;
  const address = data?.application?.address || '';
  const statusTitle = hasPending ? 'Материалы обновляются' : summary.files ? 'Файлы доступны' : summary.folders ? 'Материалы проекта' : 'Материалы готовятся';
  const statusDescription = hasPending ? 'Файлы сохранены, идёт синхронизация' : summary.files ? `${summary.filesLabel} в этой папке` : summary.folders ? 'Выберите нужный раздел' : 'Файлы появятся по мере работы';
  return <Box component="main" className="cm-page" data-track-ignore>
    <nav className="cm-topline" aria-label="Навигация по заявке">
      <button type="button" className="cm-back" onClick={() => navigate(backPath || (guest && !signedIn ? '/' : '/lk'))}>
        {guest && !signedIn ? 'На главную' : 'Мои заявки'}</button>
      <span aria-hidden="true">/</span><span>{guest ? 'Материалы по ссылке' : `Заявка №${applicationId}`}</span>
    </nav>
    <header className="cm-heading">
      <div className="cm-heading-copy">
        <Typography component="h1" level="h2">Материалы вашего проекта</Typography>
        {address ? <Typography level="body-sm" className="cm-subtitle"><LocationOnOutlined/>{address}</Typography>
          : <Typography level="body-sm" className="cm-subtitle">Материалы, фотографии и готовые файлы — в одном месте</Typography>}
      </div>
      {data && <div className="cm-project-actions">
        <div className="cm-project-status"><span className="cm-status-icon"><FolderRounded/><CheckCircleOutlineRounded/></span>
          <span><strong>{statusTitle}</strong><span>{statusDescription}</span></span></div>
        {archiveAllowed && <Button className="cm-primary-download" variant="solid" startDecorator={<DownloadRounded/>}
          aria-label="Скачать всю папку" disabled={zipBusy || !downloadable || !data.items.length} onClick={() => zip([data.folder.id])}>
          <span><strong>{zipBusy ? 'Готовим архив…' : 'Скачать всё'}</strong><span>{summary.detail}</span></span></Button>}
      </div>}
    </header>
    <Sheet variant="plain" className="cm-workspace">
      <div className="cm-section-bar">
        <nav className="cm-tabs" aria-label="Разделы материалов">
          {guest ? <Typography level="title-sm">Файлы проекта</Typography> : ['measurement', 'result', 'input', 'materials'].filter(value => value === 'materials' || !Array.isArray(data?.available_sections) || data.available_sections.includes(value)).map(value => <Button key={value} size="sm" variant="plain"
            className={value === section ? 'is-active' : undefined} aria-current={value === section ? 'page' : undefined}
            startDecorator={sectionIcons[value]} color={value === section ? 'primary' : 'neutral'} onClick={() => {initialLanding.current.add(`${objectId}:${token}`); if (value === section) setSelection({scope, id: ''}); else navigate(`/lk/storage/${objectId}/${value}`);}}>{labels[value]}</Button>)}
        </nav>
      </div>
      <div className="cm-content" aria-busy={loading}>
        {error && <Alert color="warning" role="alert" className="cm-alert">
          <div className="cm-alert-copy"><strong>Не удалось выполнить действие</strong><span>{error}</span></div>
          <Button size="sm" color="neutral" variant="outlined" disabled={loading} onClick={() => setRefresh(value => value + 1)}>Повторить</Button>
        </Alert>}
        {hasPending && <div className="cm-sync-notice" role="status"><span className="cm-status-dot"/>
          <span>Файлы сохранены и доступны. Фоновая синхронизация: {pendingCount}.</span></div>}
        {categoryNodes.length > 0 && (data?.breadcrumbs.length || 0) > 2 && <nav className="cm-path cm-deep-path" aria-label="Путь к вложенной папке">
          {data.breadcrumbs.map((crumb, index) => <React.Fragment key={crumb.id}>
            {index > 0 && <ChevronRightRounded aria-hidden="true"/>}
            {index === data.breadcrumbs.length - 1 ? <span aria-current="location">{crumb.name}</span>
              : <button type="button" onClick={() => setSelection({scope, id: crumb.id})}>{crumb.name}</button>}
          </React.Fragment>)}
        </nav>}
        <div className="cm-browser-bar">
          <div className="cm-folder-navigation">
            {categoryNodes.length > 0 ? <nav className="cm-categories" aria-label="Папки раздела">
              {categoryNodes.map(node => <button type="button" key={node.id} className={folder === node.id ? 'is-active' : undefined}
                aria-current={folder === node.id ? 'location' : undefined} onClick={() => setSelection({scope, id: node.id})}>
                {planPreview(node) ? <InsertDriveFileRounded/> : <ImageRounded/>}<span>{node.name}</span></button>)}
            </nav> : <nav className="cm-path" aria-label="Путь к папке">
              {data?.breadcrumbs.map((crumb, index) => <React.Fragment key={crumb.id}>
                {index > 0 && <ChevronRightRounded aria-hidden="true"/>}
                {index === data.breadcrumbs.length - 1 ? <span aria-current="location">{crumb.name}</span>
                  : <button type="button" onClick={() => setSelection({scope, id: crumb.id})}>{crumb.name}</button>}
              </React.Fragment>)}
              {!data && <span>{labels[section] || 'Материалы'}</span>}
            </nav>}
          </div>
          <div className="cm-tools">
            <IconButton className="cm-search-toggle" size="sm" variant="outlined" color="neutral" aria-label="Поиск и выбор файлов" aria-expanded={searchExpanded}
              onClick={() => {setSearchExpanded(value => !value); if (searchExpanded) changeQuery('');}}><SearchRounded/></IconButton>
            <label className="cm-sort"><span className="cm-sr-only">Сортировка файлов и папок</span>
              <select value={ordering} onChange={event => setOrdering(event.target.value)}>
                <option value="name">По названию</option><option value="size">По размеру</option>
              </select>
            </label>
            <IconButton className="cm-refresh" size="sm" variant="outlined" color="neutral" disabled={loading} aria-label="Обновить файлы" title="Обновить"
              onClick={() => setRefresh(value => value + 1)}>{loading ? <CircularProgress size="sm"/> : <RefreshRounded/>}</IconButton>
            <div className="cm-view-switch" role="group" aria-label="Вид файлов">
              <IconButton size="sm" variant="plain" color="neutral" className={view === 'grid' ? 'is-active' : undefined}
                aria-label="Показать карточками" aria-pressed={view === 'grid'} title="Карточки" onClick={() => setView('grid')}><GridViewRounded/></IconButton>
              <IconButton size="sm" variant="plain" color="neutral" className={view === 'list' ? 'is-active' : undefined}
                aria-label="Показать списком" aria-pressed={view === 'list'} title="Список" onClick={() => setView('list')}><ViewListRounded/></IconButton>
            </div>
          </div>
        </div>
        <div className="cm-utility-row" hidden={!searchExpanded}>
          {data && archiveAllowed && <label className="cm-select-all"><input ref={selectAllInput} type="checkbox" aria-label="Выбрать показанные файлы и папки"
            disabled={zipBusy || !selectable.length} checked={selectionView.all}
            onChange={event => setSelected(event.target.checked ? selectable.map(node => node.id) : [])}/><span>Выбрать всё</span></label>}
          <Input className="cm-search" placeholder="Поиск файлов" value={query} disabled={!data || zipBusy} size="sm"
            slotProps={{input: {'aria-label': 'Найти файлы и папки по названию'}}} startDecorator={<SearchRounded/>}
            onChange={event => changeQuery(event.target.value)}
            endDecorator={query && <IconButton size="sm" variant="plain" color="neutral" aria-label="Очистить поиск" onClick={() => changeQuery('')}><CloseRounded/></IconButton>}/>
          {data && <span className="cm-count">{query.trim() ? `${visible.length} из ${data.items.length}` : `${data.items.length} элементов`}</span>}
        </div>
        {zipHint && <div className="cm-zip-status" role="status">{zipBusy && <CircularProgress size="sm"/>}<span>{zipHint}</span>
          {zipBusy && <Button size="sm" variant="plain" color="neutral" onClick={() => {archiveController.current?.abort(); archiveController.current = null; setZipBusy(false); setZipHint('Ожидание остановлено.');}}>Остановить ожидание</Button>}</div>}
        {loading && data && <LinearProgress aria-label="Обновление файлов" className="cm-refresh-progress"/>}
        {loading && !data && <div className="cm-files is-grid" aria-label="Загрузка папки">{[1, 2, 3, 4].map(item => <Skeleton key={item} variant="rectangular" height={212} sx={{borderRadius: '12px'}}/>)}</div>}
        {data && !!visible.length && <div className={`cm-files is-${view}`} aria-label="Файлы заявки">
          {visible.map((node, index) => <React.Fragment key={node.id}>
            {groupedGrid && (index === 0 || node.kind !== visible[index - 1].kind) && <h2 className="cm-group-title">{node.kind === 'folder' ? 'Папки' : 'Файлы'}</h2>}
            <article className={`cm-file ${node.kind === 'folder' ? 'is-folder' : ''}`} data-selected={selected.includes(node.id)}>
            {archiveAllowed && canDownload(node) && <label className="cm-select-item">
              <input type="checkbox" aria-label={`Выбрать ${node.name}`} checked={selected.includes(node.id)} disabled={zipBusy}
                onChange={() => toggle(node.id)}/><span aria-hidden="true"/>
            </label>}
            <button type="button" className="cm-file-open" onClick={() => open(node)}
              disabled={node.kind === 'file' && !canDownload(node)} aria-label={`${node.kind === 'folder' ? 'Открыть папку' : previewable(node) ? 'Открыть фото' : 'Скачать файл'} ${node.name}`}>
              <span className="cm-file-visual">{node.kind === 'folder' ? <FolderRounded/> : previewable(node) && canDownload(node) && view === 'grid' && node.size <= 12 * 1024 ** 2
                ? <PhotoThumbnail node={node} resolveSource={resolveSource}/>
                : previewable(node) ? <ImageRounded/> : <InsertDriveFileRounded/>}</span>
              <span className="cm-file-description"><strong title={node.name}>{node.name}</strong>
                <span>{node.kind === 'folder' ? 'Папка' : `${materialSize(node.size)} · ${materialType(node)}`}</span>
                {node.transfer_state === 'pending' && <span className="cm-sync">Сохранён · синхронизация</span>}</span>
              {node.kind === 'folder' && <ChevronRightRounded className="cm-folder-arrow"/>}
            </button>
            {node.kind === 'file' && <div className="cm-file-actions">
              {!canDownload(node) && <Typography level="body-xs">Скачивание недоступно</Typography>}
              {canDownload(node) && <IconButton size="sm" variant="plain" color="primary" loading={activeFile === node.id} disabled={Boolean(activeFile)}
                aria-label={`Скачать ${node.name}`} title="Скачать оригинал" onClick={() => save(node)}><DownloadRounded/></IconButton>}
            </div>}
          </article></React.Fragment>)}
        </div>}
        {data && !visible.length && <div className="cm-empty"><FolderRounded/><Typography level="title-md">{query.trim() ? 'Ничего не найдено' : 'В этой папке пока нет файлов'}</Typography>
          <Typography level="body-sm">{query.trim() ? 'Проверьте название или очистите поиск в этой папке' : 'Материалы появятся здесь после добавления в вашу заявку'}</Typography>
          {query.trim() && <Button variant="outlined" color="neutral" onClick={() => changeQuery('')}>Очистить поиск</Button>}</div>}
        {!loading && !data && !error && <div className="cm-empty"><FolderRounded/><Typography level="title-md">Материалы пока недоступны</Typography></div>}
      </div>
    </Sheet>
    {data && archiveAllowed && selectedNodes.length > 0 && <aside className="cm-selection-tray" aria-label="Выбранные материалы">
      <div className="cm-selection-total"><strong>{selectedSummary.folders ? `Выбрано: ${selectedNodes.length}` : `Выбрано ${selectedSummary.filesLabel}`}</strong><span>{selectedSummary.sizeLabel || selectedSummary.detail}</span></div>
      <div className="cm-selected-preview-list">
        {selectedNodes.slice(0, 2).map(node => <div className="cm-selected-preview" key={node.id}>
          <span className="cm-selected-thumb">{node.kind === 'folder' ? <FolderRounded/> : previewable(node) && node.size <= 12 * 1024 ** 2 ? <PhotoThumbnail node={node} resolveSource={resolveSource}/> : <InsertDriveFileRounded/>}</span>
          <span className="cm-selected-name"><strong title={node.name}>{node.name}</strong><span>{node.kind === 'folder' ? 'Папка' : materialSize(node.size)}</span></span>
          <IconButton size="sm" variant="plain" color="neutral" disabled={zipBusy} aria-label={`Убрать из выбора ${node.name}`} onClick={() => toggle(node.id)}><CloseRounded/></IconButton>
        </div>)}
        {selectedNodes.length > 2 && <span className="cm-selected-more">+{selectedNodes.length - 2}</span>}
      </div>
      <Button className="cm-tray-download" variant="solid" disabled={zipBusy} startDecorator={<DownloadRounded/>} onClick={() => zip(selected)}>Скачать ZIP</Button>
      <Button className="cm-clear-selection" variant="outlined" color="neutral" disabled={zipBusy} onClick={() => setSelected([])}>Снять выбор</Button>
    </aside>}
    <div className="cm-footer-note"><LockRounded/>{guest ? 'Доступны только материалы по этой ссылке' : 'Доступны только материалы, открытые для вас'}</div>
    {viewer?.scope === viewerScope && <ImageViewer images={viewer.images} startId={viewer.startId} onClose={() => setViewer(null)} resolveSource={resolveSource} downloadFile={downloadFile}/>}
  </Box>;
}
